import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  resetVehicleAuthErrors,
  setVehicleConnectionConfirmationPending,
} from '@/actions/vehicleAuth'
import { useVehicleMakes } from '@/types/useVehicleMakes'
import ConnectSmartCar from '../smartcar/ConnectSmartCar'
import BrandSelector from '../../BrandSelector'
import ConnectVehicleConfirmation from '../ConnectVehicleConfirmation'
import { logEvent } from '@/logging'
import { useNavigation } from '@/app/hooks'
import { useRouteMatch } from 'react-router-dom'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import useUrlSearchParam from '@/hooks/useUrlSearchParam'
import LoadingAnimation from '@/components/LoadingAnimation/LoadingAnimation'
import { useAppSelector } from '@/hooks'
import { VehicleBrand } from '@/types/brand'
import { match } from 'ts-pattern'
import { AlertMessage } from '@/app/components'

const ConnectVehicleSelector = ({
  onBackClick,
  onFinish,
  titleStyle,
  selectorAreaStyle,
}: {
  onBackClick?: () => void
  onFinish?: () => void
  titleStyle?: string
  selectorAreaStyle?: string
}) => {
  const { url } = useRouteMatch()
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const { vehicleMakes, isLoading } = useVehicleMakes()
  const suggestCharger = useUrlSearchParam('suggestCharger') === 'true'
  const selectedMake = useUrlSearchParam('showSmartcar')
  const showBrandApologyAlert =
    useUrlSearchParam('showBrandApologyAlert') === 'true'

  const { vehicleConnectionConfirmationPending, errors } = useAppSelector(
    (state) => state.vehicleAuth,
  )

  const fordOauth = useFeatureFlag({
    flag: 'ford_oauth',
    experimentalValue: true,
  })

  useEffect(() => {
    logEvent('viewConnectVehicleSelector')
  }, [])

  const setSmartcarDialog = (make: VehicleBrand) => {
    logEvent('startSmartcarLoginFlow')
    dispatch(resetVehicleAuthErrors())
    navigation.pushAddParam('showSmartcar', make.name)
  }

  const reset = () => {
    navigation.pushRemoveParam('showSmartcar')
  }

  if (vehicleConnectionConfirmationPending.pending) {
    return (
      <ConnectVehicleConfirmation
        onReset={reset}
        onConfirmed={() => {
          onFinish?.()
          dispatch(
            setVehicleConnectionConfirmationPending({
              pending: false,
              make: vehicleConnectionConfirmationPending.make,
            }),
          )
        }}
      />
    )
  } else if (selectedMake) {
    // convert the url param make to a vehicle make object for the smartcar component
    const vehicleMake = vehicleMakes.find((m) => m.name === selectedMake)
    return <ConnectSmartCar vehicleMake={vehicleMake} onCancel={reset} />
  }

  const handleClick = (brand: VehicleBrand) => {
    const searchParams = new URLSearchParams(window.location.search)
    const searchParamsString = searchParams.size
      ? `?${searchParams.toString()}`
      : ''
    match({
      showApology: brand.showApology,
      ineligible: brand.ineligible,
      name: brand.name,
      fordOauth,
    })
      .with({ showApology: true }, () => {
        navigation.push(
          `${url}/ev-apology/${brand.name}?suggestCharger=${
            suggestCharger ? 'true' : 'false'
          }`,
        )
      })
      .with({ ineligible: true }, () => {
        navigation.push(`${url}/ineligible/${brand.name}`)
      })
      .with({ name: 'Tesla' }, () => {
        logEvent('startTeslaLoginFlow', { metadata: { make: brand.name } })
        navigation.push(`${url}/tesla${searchParamsString}`)
        dispatch(resetVehicleAuthErrors())
      })
      .with({ name: 'Ford', fordOauth: true }, () => {
        logEvent('startFordLoginFlow', { metadata: { make: brand.name } })
        navigation.push(`${url}/ford${searchParamsString}`)
        dispatch(resetVehicleAuthErrors())
      })
      .otherwise(() => {
        setSmartcarDialog(brand)
      })
  }

  return (
    <div>
      {showBrandApologyAlert && (
        <AlertMessage variant="success" className="mb-4">
          Thanks for letting us know! We'll keep you posted if you asked for
          updates.
        </AlertMessage>
      )}
      {isLoading ? (
        <div className="flex justify-center">
          <LoadingAnimation type="falling" centered />
        </div>
      ) : (
        <BrandSelector
          id="connect-vehicle-selector"
          brandList={vehicleMakes.filter((make) => !make.hidden)}
          onButtonSelect={handleClick}
          titleStyle={titleStyle}
          selectorAreaStyle={selectorAreaStyle}
          includeInsetBottom={false}
          handleGoBack={onBackClick}
          brandType="vehicle"
          suggestCharger={suggestCharger}
        />
      )}
    </div>
  )
}

export default ConnectVehicleSelector
